import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import { useTranslation } from '../context/TranslationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, CheckCircle2, Loader2, Lock } from 'lucide-react';
import { APP_LOGO_SRC } from '../config/branding';

const ResetPasswordPage = () => {
  const { t, language, changeLanguage } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const langParam = searchParams.get('lang');

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (langParam === 'nl' || langParam === 'en') {
      changeLanguage(langParam);
    }
  }, [langParam, changeLanguage]);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setTokenValid(false);
      setTokenError(t('auth.resetInvalidLink'));
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/auth/reset-password/${encodeURIComponent(token)}`,
          { params: { lang: langParam || language } },
        );
        if (cancelled) return;
        if (res.data?.valid) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          setTokenError(res.data?.message || t('auth.resetInvalidLink'));
        }
      } catch {
        if (!cancelled) {
          setTokenValid(false);
          setTokenError(t('auth.resetInvalidLink'));
        }
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();

    return () => { cancelled = true; };
  }, [token, t, langParam, language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError(t('auth.passwordMinLength'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
        language: langParam || language,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="mb-8">
        <img src={APP_LOGO_SRC} alt="Confi-Go" className="h-12 w-auto object-contain mx-auto" />
      </div>

      <Card className="w-full max-w-md shadow-lg">
        <CardHeader>
          <CardTitle>{t('auth.resetPasswordTitle')}</CardTitle>
          <CardDescription>{t('auth.resetPasswordDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {validating && (
            <div className="flex items-center justify-center py-8 text-gray-600">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              {t('auth.validatingLink')}
            </div>
          )}

          {!validating && !tokenValid && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{tokenError}</AlertDescription>
              </Alert>
              <Button asChild className="w-full bg-orange-600 hover:bg-orange-700">
                <Link to="/">{t('auth.backToHome')}</Link>
              </Button>
            </div>
          )}

          {!validating && tokenValid && success && (
            <div className="space-y-4">
              <Alert className="border-green-200 bg-green-50 text-green-900">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription>{t('auth.resetSuccess')}</AlertDescription>
              </Alert>
              <Button
                className="w-full bg-orange-600 hover:bg-orange-700"
                onClick={() => navigate('/')}
              >
                {t('auth.backToSignIn')}
              </Button>
            </div>
          )}

          {!validating && tokenValid && !success && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="new-password">{t('auth.newPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="new-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    minLength={8}
                    required
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t('auth.confirmPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    minLength={8}
                    required
                    disabled={loading}
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-orange-600 hover:bg-orange-700"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth.resettingPassword')}
                  </>
                ) : (
                  t('auth.resetPasswordButton')
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
